import { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import getConfig from "../../config/config";
import { axios } from "../../cores/axiosWrapper";
import useWebSocket from "@hooks/useWebSocket";
import { WEBSOCKET_MESSAGE_TYPES } from "@constants";
import { getWebsocketMessagePending, updateWebsocketMessage } from "@modules/notify";
import { updateAiWorkspaceChatResponse } from "@modules/ai-workspace";
import { snackbarUtils } from "@components_v2/snackbar-provider";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

export const WebSocketContext = createContext();

const WebSocketProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const token = useSelector(({ token }) => token.value);
    const settings = useSelector(({ settings }) => settings.stage);
    const webSocketGatewayUrl = useMemo(() => settings?.General?.websocketEndpoint?.value, [settings]);
    const stage = useSelector(({ stage }) => stage);

    const enabled = !!token && !!webSocketGatewayUrl;

    const webSocketTokenCacheRef = useRef({ token: null, expiresAt: 0 });

    const issueWebSocketToken = useCallback(async () => {
        const cache = webSocketTokenCacheRef.current;
        if (cache.token && cache.expiresAt > Date.now()) {
            return cache.token;
        }

        try {
            console.log("Requesting WebSocket token from HTTP endpoint...");

            const response = await axios.post(`${stage.endpoint}/websocket/tokens`);

            if (!response.data) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = response.data;
            // expiresAt은 UTC 문자열("YYYY-MM-DD HH:mm:ss") 형식 — dayjs.utc로 안전하게 파싱
            const parsedExpiresAt = dayjs.utc(data.expiresAt);
            webSocketTokenCacheRef.current = {
                token: data.token,
                expiresAt: parsedExpiresAt.isValid() ? parsedExpiresAt.valueOf() : 0,
            };

            console.log("WebSocket token issued successfully:", {
                tokenLength: data.token?.length ?? 0,
                expiresAt: data.expiresAt,
            });

            return data.token;
        } catch (error) {
            console.error("Failed to issue WebSocket token:", error);
            throw new Error(`토큰 발급 실패: ${error.message}`);
        }
    }, [stage.endpoint]);

    // WebSocket 훅 사용
    const { connectionStatus, lastMessage, error, sendMessage, reconnect, disconnect, isConnected, isConnecting } =
        useWebSocket(webSocketGatewayUrl, issueWebSocketToken, {
            enabled,
            onMessage: (message) => {
                console.log("WebSocket message received:", message);

                // WebSocket 메시지 처리
                if (message.type === "NOTIFY") {
                    dispatch(updateWebsocketMessage(message));
                    console.log("📢 Received NOTIFY message - refreshing message list");
                    console.log("🔄 Automatically fetching messages...");
                    handleSendMessage({
                        action: "fetchMessages",
                        data: { conditions: {} },
                    });
                } else if (message.type === "AI_CAPTION_JOB_STATUS_CHANGED") {
                    if (message.data?.status === "FAILED") {
                        snackbarUtils.error(
                            message.data.error?.message ||
                                t("common::msg::AI Caption task failed.", "AI Caption task failed."),
                        );
                    }
                } else if (message.type === "MESSAGE_LIST") {
                    dispatch(updateWebsocketMessage(message));
                    console.log("📬 Received MESSAGE_LIST - updating message store");

                    if (message.nextToken) {
                        handleSendMessage({
                            action: "fetchMessages",
                            data: { conditions: {}, nextToken: message.nextToken },
                            isFetchMore: true,
                        });
                    }
                } else if (message.type === "SESSION_CHAT_REPLY") {
                    dispatch(updateAiWorkspaceChatResponse(message));
                }
            },
            onOpen: () => {
                console.log("WebSocket connected successfully");
            },
            onClose: () => {
                console.log("WebSocket connection closed");
            },
            onError: (error) => {
                console.error("WebSocket error:", error);
            },
        });

    const handleSendMessage = useCallback(
        (data) => {
            if (data.action === "fetchMessages") {
                dispatch(
                    getWebsocketMessagePending({
                        isFetchMore: data.isFetchMore || false,
                    }),
                );
            }
            sendMessage(data);
        },
        [sendMessage, dispatch],
    );

    useEffect(() => {
        if (!enabled) return;
        return () => {
            console.log("WebSocket disabled, disconnecting...");
            webSocketTokenCacheRef.current = { token: null, expiresAt: 0 };
            disconnect();
        };
    }, [enabled, disconnect]);

    // 브라우저가 닫힐 때 WebSocket disconnect
    useEffect(() => {
        if (!enabled) return;
        const handleUnload = () => {
            console.log("Browser closing, disconnecting WebSocket...");
            disconnect();
        };
        window.addEventListener("unload", handleUnload);

        return () => {
            window.removeEventListener("unload", handleUnload);
        };
    }, [enabled, disconnect]);

    const contextValue = {
        sendMessage: handleSendMessage,
        connectionStatus,
        lastMessage,
        error,
        reconnect,
        disconnect,
        isConnected,
        isConnecting,
    };

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export default WebSocketProvider;
